import { useEffect, useMemo, useState } from "react";
import AppShell, { SectionCard, StatCard } from "../components/AppShell";
import { api, API, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Briefcase, Plus, Calendar, UserPlus, CheckCircle, Trash, PaperPlaneTilt, UserCircle,
  Sparkle, MagicWand, ChartBar, Funnel, TrendUp, MapPin, Buildings, Clock, UploadSimple, FileText, Warning, PencilSimple, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ScheduleRoundDialog, HiringCommitteeDialog } from "../components/InterviewLoopDialogs";
import { DateInput } from "../components/ui/date-input";

const err = (e) => toast.error(formatApiError(e?.response?.data?.detail));

const REQ_STATUS = {
  draft: "bg-zinc-100 text-zinc-700", open: "bg-emerald-100 text-emerald-700",
  on_hold: "bg-amber-100 text-amber-800", closed: "bg-blue-100 text-blue-700",
  cancelled: "bg-red-100 text-red-700",
};
const CAND_STAGE = {
  applied: "bg-zinc-100 text-zinc-700", screening: "bg-blue-100 text-blue-700",
  shortlisted: "bg-cyan-100 text-cyan-700", interview: "bg-amber-100 text-amber-800",
  offer_pending: "bg-violet-100 text-violet-700", offer_sent: "bg-pink-100 text-pink-700",
  offer_accepted: "bg-emerald-100 text-emerald-700", offer_declined: "bg-red-100 text-red-700",
  hired: "bg-teal-100 text-teal-700", rejected: "bg-red-200 text-red-800",
  withdrawn: "bg-zinc-200 text-zinc-600",
};
const OFFER_STATUS = {
  draft: "bg-zinc-100 text-zinc-700", sent: "bg-blue-100 text-blue-700",
  accepted: "bg-emerald-100 text-emerald-700", declined: "bg-red-100 text-red-700",
  withdrawn: "bg-zinc-200 text-zinc-600", expired: "bg-amber-100 text-amber-800",
};

const SCORE_COLOR = (s) => s == null ? "text-zinc-400"
  : s >= 80 ? "text-emerald-700"
  : s >= 65 ? "text-blue-700"
  : s >= 50 ? "text-amber-700" : "text-rose-700";
const SCORE_BG = (s) => s == null ? "bg-zinc-50 border-zinc-200"
  : s >= 80 ? "bg-emerald-50 border-emerald-200"
  : s >= 65 ? "bg-blue-50 border-blue-200"
  : s >= 50 ? "bg-amber-50 border-amber-200" : "bg-rose-50 border-rose-200";

// ─── Recruitment Hub Overview ─────────────────────────────────────────────
export default function RecruitmentOverview() {
  const [reqs, setReqs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [offers, setOffers] = useState([]);
  const [insights, setInsights] = useState(null);

  const load = async () => {
    try {
      const [r, c, o, i] = await Promise.all([
        api.get("/requisitions"),
        api.get("/candidates"),
        api.get("/offers"),
        api.get("/requisitions/_/insights").catch(() => ({ data: null })),
      ]);
      setReqs(r.data); setCandidates(c.data); setOffers(o.data);
      setInsights(i.data);
    } catch (e) { err(e); }
  };
  useEffect(() => { load(); }, []);

  const openReqs = reqs.filter(r => r.status === "open");
  const totalOpenings = openReqs.reduce((s, r) => s + (r.openings - r.openings_filled), 0);
  const pending = candidates.filter(c => !["hired","rejected","withdrawn","offer_declined"].includes(c.stage)).length;
  const hired = candidates.filter(c => c.stage === "hired").length;
  const acceptedThisMonth = offers.filter(o => o.status === "accepted" && (o.decided_at || "").slice(0,7) === new Date().toISOString().slice(0,7)).length;
  const offerAcceptRate = useMemo(() => {
    const decided = offers.filter(o => ["accepted","declined"].includes(o.status));
    if (!decided.length) return null;
    return Math.round(100 * offers.filter(o => o.status === "accepted").length / decided.length);
  }, [offers]);

  // Pipeline funnel — same order as canonical stages
  const FUNNEL_ORDER = ["applied","screening","shortlisted","interview","offer_sent","offer_accepted","hired"];
  const funnelData = FUNNEL_ORDER.map(s => ({
    stage: s, count: candidates.filter(c => c.stage === s).length,
  }));
  const funnelMax = Math.max(...funnelData.map(d => d.count), 1);

  const sourceMix = useMemo(() => {
    const m = {};
    candidates.forEach(c => { m[c.source || "other"] = (m[c.source || "other"] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [candidates]);
  const sourceTotal = candidates.length || 1;
  const SRC_COLOR = {
    referral: "bg-emerald-500", linkedin: "bg-blue-500", careers_page: "bg-violet-500",
    naukri: "bg-amber-500", indeed: "bg-sky-500", agency: "bg-rose-500", other: "bg-zinc-400",
  };

  return (
    <AppShell title="Recruitment">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Open requisitions" value={openReqs.length} hint={`${totalOpenings} openings`} testid="stat-open-reqs"/>
        <StatCard label="Active candidates" value={pending} hint="In pipeline" testid="stat-active-cands"/>
        <StatCard label="Offer acceptance" value={offerAcceptRate == null ? "—" : `${offerAcceptRate}%`} hint={`${offers.length} offers total`} testid="stat-offer-accept"/>
        <StatCard label="Hired (all time)" value={hired} hint={`${acceptedThisMonth} this month`} testid="stat-hired"/>
      </div>

      {/* AI Top picks across all open reqs */}
      {insights?.top_picks?.length > 0 && (
        <SectionCard
          testid="section-ai-top-picks"
          title={<span className="flex items-center gap-2"><Sparkle weight="fill" className="text-violet-600"/>AI top picks across open roles</span>}
          subtitle="Auto-ranked by SaarOne AI. Highest-fit candidates currently in pipeline."
          action={<Link to="/app/recruitment/insights"><Button size="sm" variant="outline" className="gap-1.5"><ChartBar size={14}/>AI Insights</Button></Link>}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ai-top-picks-grid">
            {insights.top_picks.slice(0, 8).map(p => (
              <Link key={p.id} to={`/app/recruitment/requisitions`}
                    className={`rounded-lg border p-3 hover:shadow-md transition ${SCORE_BG(p.score)}`}
                    data-testid={`top-pick-${p.id}`}>
                <div className="flex items-start justify-between mb-1">
                  <div className="font-semibold text-sm truncate">{p.name}</div>
                  <div className={`text-lg font-bold tabular-nums ${SCORE_COLOR(p.score)}`}>{p.score}</div>
                </div>
                <div className="text-xs text-zinc-600 truncate">{p.requisition_title}</div>
                <div className="flex items-center gap-1.5 mt-2 text-[10px] text-zinc-500">
                  <Badge variant="outline" className="text-[9px] py-0">{p.stage.replace(/_/g," ")}</Badge>
                  {p.current_company && <span>· {p.current_company}</span>}
                </div>
              </Link>
            ))}
          </div>
        </SectionCard>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        {/* Pipeline funnel */}
        <div className="lg:col-span-2">
          <SectionCard
            title={<span className="flex items-center gap-2"><Funnel size={16} className="text-blue-600"/>Pipeline funnel</span>}
            subtitle="Where candidates are right now across every open role."
            testid="section-pipeline-funnel">
            <div className="space-y-2" data-testid="pipeline-funnel">
              {funnelData.map(d => (
                <div key={d.stage} className="flex items-center gap-3" data-testid={`funnel-${d.stage}`}>
                  <div className="w-32 text-xs capitalize text-zinc-600 shrink-0">{d.stage.replace(/_/g," ")}</div>
                  <div className="flex-1 bg-zinc-100 rounded h-7 overflow-hidden relative">
                    <div className={`h-full transition-all flex items-center justify-end pr-2 text-xs font-medium text-white ${CAND_STAGE[d.stage]?.includes("emerald") ? "bg-emerald-500" : CAND_STAGE[d.stage]?.includes("blue") ? "bg-blue-500" : CAND_STAGE[d.stage]?.includes("amber") ? "bg-amber-500" : CAND_STAGE[d.stage]?.includes("violet") ? "bg-violet-500" : CAND_STAGE[d.stage]?.includes("pink") ? "bg-pink-500" : CAND_STAGE[d.stage]?.includes("teal") ? "bg-teal-500" : "bg-zinc-400"}`}
                         style={{ width: `${Math.max(4, (d.count / funnelMax) * 100)}%` }}>
                      {d.count > 0 && <span>{d.count}</span>}
                    </div>
                  </div>
                  <div className="w-10 text-right text-xs font-semibold tabular-nums">{d.count}</div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>

        {/* Source mix */}
        <SectionCard
          title={<span className="flex items-center gap-2"><TrendUp size={16} className="text-emerald-600"/>Sources mix</span>}
          subtitle="Where candidates come from."
          testid="section-source-mix">
          <div className="flex h-3 rounded-full overflow-hidden mb-3" data-testid="source-bar">
            {sourceMix.map(([src, n]) => (
              <div key={src}
                   className={SRC_COLOR[src] || "bg-zinc-400"}
                   title={`${src}: ${n}`}
                   style={{ width: `${(n / sourceTotal) * 100}%` }}/>
            ))}
          </div>
          <div className="space-y-1.5">
            {sourceMix.slice(0, 6).map(([src, n]) => (
              <div key={src} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${SRC_COLOR[src] || "bg-zinc-400"}`}/>
                  <span className="capitalize text-zinc-700">{src.replace(/_/g," ")}</span>
                </div>
                <span className="tabular-nums font-medium text-zinc-900">{n} <span className="text-zinc-500">({Math.round(100*n/sourceTotal)}%)</span></span>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Open requisitions as cards */}
      <SectionCard
        title="Open job openings"
        subtitle="Click any role to open its full pipeline & AI workspace."
        testid="section-open-reqs"
        action={<Link to="/app/recruitment/requisitions"><Button size="sm" className="gap-1.5"><Briefcase size={14}/>All requisitions</Button></Link>}>
        {openReqs.length === 0 && <div className="text-sm text-zinc-500 py-6 text-center">No open requisitions.</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="open-req-cards">
          {openReqs.slice(0, 9).map(r => {
            const reqCands = candidates.filter(c => c.requisition_id === r.id);
            const active = reqCands.filter(c => !["hired","rejected","withdrawn","offer_declined"].includes(c.stage));
            const topScore = Math.max(0, ...reqCands.map(c => c.ai_role_match_score || 0));
            const daysOpen = r.opened_at ? Math.floor((Date.now() - new Date(r.opened_at).getTime()) / 86400000) : null;
            return (
              <Link key={r.id} to={`/app/recruitment/requisitions/${r.id}`}
                    className="block bg-white border border-zinc-200 rounded-lg p-4 hover:border-teal-400 hover:shadow-md transition"
                    data-testid={`req-card-${r.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-zinc-900 truncate">{r.title}</div>
                    <div className="font-mono text-[10px] text-zinc-400 mt-0.5">{r.code}</div>
                  </div>
                  <Badge className={REQ_STATUS[r.status]}>{r.status}</Badge>
                </div>
                <div className="flex items-center gap-3 text-xs text-zinc-600 mb-3">
                  {r.department && <span className="flex items-center gap-1"><Buildings size={12}/>{r.department}</span>}
                  {r.location && <span className="flex items-center gap-1"><MapPin size={12}/>{r.location}</span>}
                  {daysOpen != null && <span className="flex items-center gap-1"><Clock size={12}/>{daysOpen}d open</span>}
                </div>
                <div className="grid grid-cols-3 gap-2 text-center mb-2">
                  <div className="bg-zinc-50 rounded py-1.5">
                    <div className="text-sm font-bold">{active.length}</div>
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500">Active</div>
                  </div>
                  <div className="bg-zinc-50 rounded py-1.5">
                    <div className="text-sm font-bold tabular-nums">{r.openings_filled}/{r.openings}</div>
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500">Filled</div>
                  </div>
                  <div className={`rounded py-1.5 ${topScore > 0 ? SCORE_BG(topScore) : "bg-zinc-50"}`}>
                    <div className={`text-sm font-bold ${SCORE_COLOR(topScore > 0 ? topScore : null)}`}>{topScore > 0 ? topScore : "—"}</div>
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500">Top AI</div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </SectionCard>
    </AppShell>
  );
}

// ─── Requisitions list + create ──────────────────────────────────────────
export function Requisitions() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(blank());
  function blank() {
    return { title:"", department:"", location:"", employment_type:"full_time", work_mode:"wfo",
      openings:1, priority:"medium", hiring_manager_id:"", salary_min:"", salary_max:"",
      description_markdown:"", skills:"", is_public:true };
  }

  const load = async () => {
    try { const [r, e] = await Promise.all([api.get("/requisitions"), api.get("/employees")]);
      setRows(r.data); setEmployees(e.data); } catch (e) { err(e); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = { ...f, skills: f.skills ? f.skills.split(",").map(s=>s.trim()) : [] };
      if (!body.hiring_manager_id) delete body.hiring_manager_id;
      if (body.salary_min === "") delete body.salary_min; else body.salary_min = Number(body.salary_min);
      if (body.salary_max === "") delete body.salary_max; else body.salary_max = Number(body.salary_max);
      await api.post("/requisitions", body);
      toast.success("Requisition created"); setOpen(false); setF(blank()); load();
    } catch (e) { err(e); }
  };
  const setStatus = async (id, status) => { try { await api.post(`/requisitions/${id}/status`, { status }); load(); } catch (e) { err(e); } };

  return (
    <AppShell title="Job requisitions">
      <SectionCard title="Requisitions" subtitle="Create and manage open roles."
        action={<Button size="sm" onClick={()=>setOpen(true)} data-testid="new-req-btn" className="gap-1.5"><Plus size={14} weight="bold"/> New requisition</Button>}
        testid="section-reqs">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Code</TableHead><TableHead>Title</TableHead>
            <TableHead>Department</TableHead><TableHead>Type</TableHead>
            <TableHead className="text-right">Openings</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-6 text-zinc-500">No requisitions.</TableCell></TableRow>}
            {rows.map(r => (
              <TableRow key={r.id} data-testid={`req-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.code}</TableCell>
                <TableCell className="font-medium"><Link to={`/app/recruitment/requisitions/${r.id}`} className="hover:underline">{r.title}</Link></TableCell>
                <TableCell>{r.department || "—"}</TableCell>
                <TableCell className="text-xs capitalize">{(r.employment_type || "—").replace("_"," ")} · {r.work_mode || "—"}</TableCell>
                <TableCell className="text-right tabular-nums">{r.openings_filled}/{r.openings}</TableCell>
                <TableCell><Badge className={REQ_STATUS[r.status]}>{r.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end flex-wrap">
                    {r.status === "draft" && <Button size="sm" onClick={()=>setStatus(r.id,"open")} data-testid={`req-open-${r.id}`}>Open</Button>}
                    {r.status === "open" && <Button size="sm" variant="secondary" onClick={()=>setStatus(r.id,"on_hold")}>Hold</Button>}
                    {(r.status === "open" || r.status === "on_hold") && <Button size="sm" variant="outline" onClick={()=>setStatus(r.id,"closed")}>Close</Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="new-req-dialog">
          <DialogHeader><DialogTitle>New job requisition</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 max-h-[70vh] overflow-y-auto pr-2">
            <div><Label>Title</Label><Input value={f.title} onChange={e=>setF({...f,title:e.target.value})} placeholder="Senior Backend Engineer" data-testid="req-title"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Department</Label><Input value={f.department} onChange={e=>setF({...f,department:e.target.value})}/></div>
              <div><Label>Location</Label><Input value={f.location} onChange={e=>setF({...f,location:e.target.value})}/></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Employment type</Label>
                <Select value={f.employment_type} onValueChange={v=>setF({...f,employment_type:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="full_time">Full time</SelectItem><SelectItem value="part_time">Part time</SelectItem><SelectItem value="contract">Contract</SelectItem><SelectItem value="intern">Intern</SelectItem><SelectItem value="consultant">Consultant</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Work mode</Label>
                <Select value={f.work_mode} onValueChange={v=>setF({...f,work_mode:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="wfo">Office</SelectItem><SelectItem value="wfh">Remote</SelectItem><SelectItem value="hybrid">Hybrid</SelectItem><SelectItem value="field">Field</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Openings</Label><Input type="number" min="1" value={f.openings} onChange={e=>setF({...f,openings:Number(e.target.value)})}/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Salary min (₹)</Label><Input type="number" value={f.salary_min} onChange={e=>setF({...f,salary_min:e.target.value})}/></div>
              <div><Label>Salary max (₹)</Label><Input type="number" value={f.salary_max} onChange={e=>setF({...f,salary_max:e.target.value})}/></div>
            </div>
            <div><Label>Hiring manager</Label>
              <Select value={f.hiring_manager_id} onValueChange={v=>setF({...f,hiring_manager_id:v})}>
                <SelectTrigger><SelectValue placeholder="Pick manager"/></SelectTrigger>
                <SelectContent className="max-h-72">{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} — {e.job_title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Skills (comma separated)</Label><Input value={f.skills} onChange={e=>setF({...f,skills:e.target.value})} placeholder="Python, FastAPI, MongoDB"/></div>
            <div><Label>Description (markdown)</Label><Textarea rows={5} value={f.description_markdown} onChange={e=>setF({...f,description_markdown:e.target.value})}/></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="req-save">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

// ─── Requisition detail + candidate pipeline (Kanban-ish) ────────────────
const PIPE_STAGES = ["applied","screening","shortlisted","interview","offer_pending","offer_sent","offer_accepted","hired","rejected"];

export function RequisitionDetail() {
  const { id } = useParams();
  const [req, setReq] = useState(null);
  const [cands, setCands] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [nf, setNF] = useState({ name:"", email:"", phone:"", current_company:"", expected_ctc:"", source:"linkedin" });
  const [aiBusy, setAIBusy] = useState(false);
  const [view, setView] = useState("ranked"); // ranked | kanban
  const [filters, setFilters] = useState({ minScore: 0, source: "all", stage: "all" });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    try {
      const [r, c, a] = await Promise.all([
        api.get(`/requisitions/${id}`),
        api.get(`/candidates?requisition_id=${id}`),
        api.get(`/requisitions/${id}/analytics`).catch(() => ({ data: null })),
      ]);
      setReq(r.data); setCands(c.data); setAnalytics(a.data);
    } catch (e) { err(e); }
  };
  useEffect(() => { load(); }, [id]);

  const add = async () => {
    try {
      const body = { ...nf, requisition_id: id };
      if (body.expected_ctc === "") delete body.expected_ctc; else body.expected_ctc = Number(body.expected_ctc);
      await api.post("/candidates", body);
      toast.success("Candidate added"); setOpen(false);
      setNF({ name:"", email:"", phone:"", current_company:"", expected_ctc:"", source:"linkedin" });
      load();
    } catch (e) { err(e); }
  };
  const moveStage = async (cid, stage) => {
    try { await api.post(`/candidates/${cid}/stage`, { stage }); load(); } catch (e) { err(e); }
  };
  const setReqStatus = async (newStatus) => {
    try {
      await api.post(`/requisitions/${id}/status`, { status: newStatus });
      toast.success(`Requisition status set to ${newStatus.replace(/_/g, " ")}`);
      load();
    } catch (e) { err(e); }
  };
  const runAIAnalysis = async () => {
    setAIBusy(true);
    try {
      const { data } = await api.post(`/requisitions/${id}/run-ai-analysis`);
      toast.success(`AI ranked ${data.scored} candidates — ${data.distribution.strong} strong fits.`);
      load();
    } catch (e) { err(e); } finally { setAIBusy(false); }
  };

  // Build funnel (uses analytics endpoint when present, falls back to local count)
  const funnelStages = ["applied","screening","shortlisted","interview","offer_sent","offer_accepted","hired"];
  const funnelData = funnelStages.map(s => {
    const fromApi = analytics?.funnel?.find(f => f.stage === s);
    return { stage: s, count: fromApi ? fromApi.count : cands.filter(c => c.stage === s).length };
  });
  const funnelMax = Math.max(...funnelData.map(d => d.count), 1);

  const filtered = useMemo(() => {
    return cands.filter(c =>
      (c.ai_role_match_score == null ? 0 : c.ai_role_match_score) >= filters.minScore
      && (filters.source === "all" || c.source === filters.source)
      && (filters.stage === "all" || c.stage === filters.stage)
    );
  }, [cands, filters]);
  const rankedList = useMemo(() => {
    return [...filtered].sort((a, b) =>
      (b.ai_role_match_score || 0) - (a.ai_role_match_score || 0)
    );
  }, [filtered]);

  const sources = useMemo(() => [...new Set(cands.map(c => c.source).filter(Boolean))], [cands]);

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAll = () => setSelectedIds(new Set(rankedList.map(c => c.id)));
  const clearSelection = () => setSelectedIds(new Set());

  const bulkAction = async (action, payload = {}) => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/recruitment/candidates/bulk-action", {
        candidate_ids: [...selectedIds],
        action,
        ...payload,
      });
      toast.success(`${data.affected} candidate(s) updated`);
      clearSelection();
      load();
    } catch (e) { err(e); }
    finally { setBulkBusy(false); }
  };
  const bulkAdvance = () => {
    const stage = window.prompt("Move to stage (applied/screening/shortlisted/interview/offer_pending/offer_sent/offer_accepted/hired):", "shortlisted");
    if (stage) bulkAction("advance", { target_stage: stage });
  };
  const bulkReject = () => {
    const reason = window.prompt("Reason for rejection (required):");
    if (reason && reason.trim()) bulkAction("reject", { reason: reason.trim() });
  };
  const bulkTag = () => {
    const tag = window.prompt("Tag to add to selected candidates:");
    if (tag && tag.trim()) bulkAction("tag", { tag: tag.trim() });
  };
  const lastAnalyzedAt = useMemo(() => {
    let t = null;
    cands.forEach(c => {
      const at = c.ai_parsed?.last_analyzed_at;
      if (at && (!t || at > t)) t = at;
    });
    return t;
  }, [cands]);

  return (
    <AppShell title={req ? `${req.code} · ${req.title}` : "Requisition"}>
      {/* Header card */}
      {req && (
        <SectionCard testid="section-req-detail"
          title={<span className="flex items-center gap-2">{req.title} <Badge className={REQ_STATUS[req.status]}>{req.status}</Badge></span>}
          subtitle={req.description_markdown?.slice(0, 140) || "—"}
          action={<div className="flex gap-2 flex-wrap">
            <select
              value={req.status}
              onChange={(e) => {
                const ns = e.target.value;
                if (ns === req.status) return;
                if (window.confirm(`Change requisition status from "${req.status.replace(/_/g, " ")}" to "${ns.replace(/_/g, " ")}"?`)) {
                  setReqStatus(ns);
                } else {
                  // revert <select> to current — React will re-render from req.status
                  load();
                }
              }}
              data-testid="req-status-select"
              className="text-xs border border-zinc-300 rounded h-8 px-2 bg-white capitalize"
              title="Change requisition status"
            >
              <option value="draft">Draft</option>
              <option value="open">Open</option>
              <option value="on_hold">On hold</option>
              <option value="closed">Closed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <Button size="sm" variant="outline" onClick={()=>setBulkOpen(true)} className="gap-1.5" data-testid="bulk-upload-btn">
              <UploadSimple size={14}/>Bulk upload CVs
            </Button>
            <Button size="sm" variant="outline" onClick={()=>setOpen(true)} className="gap-1.5" data-testid="add-cand-btn"><UserPlus size={14}/>Add candidate</Button>
            <Button size="sm" variant="outline" onClick={()=>setEditOpen(true)} className="gap-1.5" data-testid="edit-req-btn" disabled={req.status === "cancelled"}><PencilSimple size={14}/>Edit</Button>
            <Button size="sm" variant="ghost" onClick={()=>setHistoryOpen(true)} className="gap-1 px-2" title="Edit history" data-testid="req-history-btn"><ClockCounterClockwise size={14}/></Button>
            <Button size="sm" onClick={runAIAnalysis} disabled={aiBusy} className="gap-1.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:from-violet-700 hover:to-fuchsia-700" data-testid="run-ai-btn">
              <MagicWand size={14} weight="fill"/>{aiBusy ? "Analyzing…" : "Run AI Analysis"}
            </Button>
          </div>}>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
            <div><div className="text-xs text-zinc-500">Department</div><div className="font-medium">{req.department || "—"}</div></div>
            <div><div className="text-xs text-zinc-500">Location</div><div className="font-medium">{req.location || "—"}</div></div>
            <div><div className="text-xs text-zinc-500">Work mode</div><div className="font-medium capitalize">{req.work_mode}</div></div>
            <div><div className="text-xs text-zinc-500">Experience</div><div className="font-medium">{req.experience_min ?? "—"}–{req.experience_max ?? "—"} yrs</div></div>
            <div><div className="text-xs text-zinc-500">Salary band</div><div className="font-medium text-xs">{req.currency} {(req.salary_min || 0).toLocaleString("en-IN")} – {(req.salary_max || 0).toLocaleString("en-IN")}</div></div>
            <div><div className="text-xs text-zinc-500">Openings</div><div className="font-medium tabular-nums">{req.openings_filled}/{req.openings}</div></div>
          </div>
          {(req.skills || []).length > 0 && (
            <div className="mt-4 pt-4 border-t border-zinc-100">
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">Required skills</div>
              <div className="flex flex-wrap gap-1.5">
                {(req.skills || []).map((s, i) => <span key={i} className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded">{s}</span>)}
              </div>
            </div>
          )}
        </SectionCard>
      )}

      {/* KPI tiles + funnel + AI distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <SectionCard title={<span className="flex items-center gap-2"><Funnel size={16} className="text-blue-600"/>Pipeline funnel</span>}
                     testid="section-req-funnel">
          <div className="space-y-2">
            {funnelData.map(d => (
              <div key={d.stage} className="flex items-center gap-2 text-xs" data-testid={`req-funnel-${d.stage}`}>
                <div className="w-24 capitalize text-zinc-600 shrink-0">{d.stage.replace(/_/g," ")}</div>
                <div className="flex-1 bg-zinc-100 rounded h-5 overflow-hidden">
                  <div className={`h-full ${CAND_STAGE[d.stage]?.split(" ")[0]?.replace("bg-","bg-").replace("-100","-500") || "bg-zinc-400"}`}
                       style={{ width: `${Math.max(2, (d.count / funnelMax) * 100)}%` }}/>
                </div>
                <div className="w-8 text-right font-semibold tabular-nums">{d.count}</div>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title={<span className="flex items-center gap-2"><Sparkle weight="fill" size={16} className="text-violet-600"/>AI score distribution</span>}
                     subtitle={lastAnalyzedAt ? `Last analyzed ${new Date(lastAnalyzedAt).toLocaleString()}` : "Run AI Analysis to score candidates"}
                     testid="section-ai-dist">
          {(() => {
            const buckets = { strong: 0, good: 0, average: 0, weak: 0, unscored: 0 };
            cands.forEach(c => {
              const s = c.ai_role_match_score;
              if (s == null) buckets.unscored++;
              else if (s >= 80) buckets.strong++;
              else if (s >= 65) buckets.good++;
              else if (s >= 50) buckets.average++;
              else buckets.weak++;
            });
            const rows = [
              ["Strong fit (80+)", buckets.strong, "bg-emerald-500"],
              ["Good fit (65-79)", buckets.good, "bg-blue-500"],
              ["Average (50-64)", buckets.average, "bg-amber-500"],
              ["Weak (<50)", buckets.weak, "bg-rose-500"],
              ["Not analyzed yet", buckets.unscored, "bg-zinc-300"],
            ];
            const max = Math.max(...rows.map(r => r[1]), 1);
            return rows.map(([label, n, color]) => (
              <div key={label} className="flex items-center gap-2 text-xs mb-1.5">
                <div className="w-36 text-zinc-700 shrink-0">{label}</div>
                <div className="flex-1 bg-zinc-100 rounded h-4 overflow-hidden">
                  <div className={`h-full ${color}`} style={{ width: `${(n / max) * 100}%` }}/>
                </div>
                <div className="w-6 text-right font-semibold tabular-nums">{n}</div>
              </div>
            ));
          })()}
        </SectionCard>

        <SectionCard title={<span className="flex items-center gap-2"><TrendUp size={16} className="text-emerald-600"/>Source mix</span>}
                     testid="section-req-sources">
          {(() => {
            const m = {};
            cands.forEach(c => { m[c.source || "other"] = (m[c.source || "other"] || 0) + 1; });
            const entries = Object.entries(m).sort((a, b) => b[1] - a[1]);
            const total = cands.length || 1;
            const COL = { referral:"bg-emerald-500", linkedin:"bg-blue-500", careers_page:"bg-violet-500", naukri:"bg-amber-500", indeed:"bg-sky-500", agency:"bg-rose-500", other:"bg-zinc-400" };
            return (<>
              <div className="flex h-3 rounded-full overflow-hidden mb-3">
                {entries.map(([s, n]) => <div key={s} className={COL[s] || "bg-zinc-400"} title={`${s}: ${n}`} style={{ width: `${(n/total)*100}%` }}/>)}
              </div>
              {entries.map(([s, n]) => (
                <div key={s} className="flex items-center justify-between text-xs mb-1">
                  <div className="flex items-center gap-2"><span className={`inline-block w-2 h-2 rounded-full ${COL[s] || "bg-zinc-400"}`}/><span className="capitalize">{s.replace(/_/g," ")}</span></div>
                  <span className="font-medium tabular-nums">{n}</span>
                </div>
              ))}
              {entries.length === 0 && <div className="text-xs text-zinc-500 py-2">No candidates yet.</div>}
            </>);
          })()}
        </SectionCard>
      </div>

      {/* Candidate workspace: ranked list ⇆ kanban */}
      <SectionCard
        testid="section-pipeline"
        title="Candidates"
        subtitle={`${filtered.length} of ${cands.length} shown — sorted by AI score`}
        action={<div className="flex flex-wrap gap-2 items-center">
          <Select value={String(filters.minScore)} onValueChange={(v) => setFilters(f=>({...f, minScore: Number(v)}))}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-min-score"><SelectValue placeholder="Min score"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Any score</SelectItem>
              <SelectItem value="50">50+ score</SelectItem>
              <SelectItem value="65">65+ score</SelectItem>
              <SelectItem value="80">80+ (strong)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.source} onValueChange={(v) => setFilters(f=>({...f, source: v}))}>
            <SelectTrigger className="h-8 w-28 text-xs" data-testid="filter-source"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {sources.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g," ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.stage} onValueChange={(v) => setFilters(f=>({...f, stage: v}))}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-stage"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {PIPE_STAGES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g," ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex bg-zinc-100 rounded-md p-0.5">
            <button onClick={() => setView("ranked")} className={`text-xs px-2.5 py-1 rounded ${view==="ranked" ? "bg-white shadow-sm font-medium" : "text-zinc-600"}`} data-testid="view-ranked">Ranked</button>
            <button onClick={() => setView("kanban")} className={`text-xs px-2.5 py-1 rounded ${view==="kanban" ? "bg-white shadow-sm font-medium" : "text-zinc-600"}`} data-testid="view-kanban">Kanban</button>
          </div>
        </div>}>

        {view === "ranked" ? (
          <div className="space-y-2" data-testid="ranked-list">
            {/* Bulk-action toolbar */}
            {rankedList.length > 0 && (
              <div className="flex items-center justify-between p-2 bg-zinc-50 border border-dashed border-zinc-300 rounded text-xs" data-testid="bulk-toolbar">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedIds.size > 0 && selectedIds.size === rankedList.length}
                    onChange={() => selectedIds.size === rankedList.length ? clearSelection() : selectAll()}
                    data-testid="bulk-select-all"
                  />
                  <span className="text-zinc-600">
                    {selectedIds.size === 0 ? "Select all" : `${selectedIds.size} selected`}
                  </span>
                </label>
                {selectedIds.size > 0 && (
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={bulkAdvance} disabled={bulkBusy} data-testid="bulk-advance">Advance stage</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={bulkTag} disabled={bulkBusy} data-testid="bulk-tag">Tag</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs text-rose-700 border-rose-300" onClick={bulkReject} disabled={bulkBusy} data-testid="bulk-reject">Reject</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={clearSelection} data-testid="bulk-clear">Clear</Button>
                  </div>
                )}
              </div>
            )}
            {rankedList.length === 0 && <div className="text-sm text-zinc-500 text-center py-6">No candidates match the filters.</div>}
            {rankedList.map((c, idx) => (
              <CandidateRow
                key={c.id}
                rank={idx + 1}
                c={c}
                onMove={moveStage}
                onAfter={load}
                selected={selectedIds.has(c.id)}
                onToggleSelect={() => toggleSelect(c.id)}
              />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="flex gap-3 min-w-[1400px] pb-2">
              {PIPE_STAGES.map(stage => {
                const items = filtered.filter(c => c.stage === stage);
                return (
                  <div key={stage} className="flex-1 min-w-[180px] bg-zinc-50 border border-zinc-200 rounded-lg p-2" data-testid={`col-${stage}`}>
                    <div className="flex items-center justify-between mb-2 px-1">
                      <span className="text-xs font-semibold uppercase tracking-wide text-zinc-600">{stage.replace(/_/g," ")}</span>
                      <Badge variant="outline" className="text-xs">{items.length}</Badge>
                    </div>
                    <div className="space-y-2">
                      {items.map(c => <CandidateCard key={c.id} c={c} stages={PIPE_STAGES} onMove={moveStage} onAfter={load} />)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </SectionCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-cand-dialog">
          <DialogHeader><DialogTitle>Add candidate</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Name</Label><Input value={nf.name} onChange={e=>setNF({...nf,name:e.target.value})} data-testid="cand-name"/></div>
            <div><Label>Email</Label><Input type="email" value={nf.email} onChange={e=>setNF({...nf,email:e.target.value})} data-testid="cand-email"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={nf.phone} onChange={e=>setNF({...nf,phone:e.target.value})}/></div>
              <div><Label>Source</Label>
                <Select value={nf.source} onValueChange={v=>setNF({...nf,source:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="careers_page">Careers page</SelectItem><SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="linkedin">LinkedIn</SelectItem><SelectItem value="naukri">Naukri</SelectItem>
                    <SelectItem value="indeed">Indeed</SelectItem><SelectItem value="agency">Agency</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Current company</Label><Input value={nf.current_company} onChange={e=>setNF({...nf,current_company:e.target.value})}/></div>
            <div><Label>Expected CTC (₹/yr)</Label><Input type="number" value={nf.expected_ctc} onChange={e=>setNF({...nf,expected_ctc:e.target.value})}/></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={add} data-testid="cand-save">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <BulkUploadModal open={bulkOpen} onClose={() => setBulkOpen(false)} requisitionId={id} onDone={load}/>
      {editOpen && (
        <EditRequisitionDialog req={req} onClose={() => setEditOpen(false)} onSaved={(updated) => { setReq(updated); load(); setEditOpen(false); }} />
      )}
      {historyOpen && (
        <RequisitionHistoryDialog requisitionId={id} onClose={() => setHistoryOpen(false)} />
      )}
    </AppShell>
  );
}

// ─── Bulk CV upload modal ────────────────────────────────────────────────
function BulkUploadModal({ open, onClose, requisitionId, onDone }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const reset = () => { setFiles([]); setResult(null); setBusy(false); };
  const closeAll = () => { onClose(); reset(); };

  const accept = ".pdf,.docx";
  const MAX = 50;
  const MAX_MB = 5;

  const addFiles = (incoming) => {
    const arr = Array.from(incoming || []);
    const valid = [];
    const rejected = [];
    for (const f of arr) {
      const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
      if (![".pdf", ".docx"].includes(ext)) rejected.push(`${f.name} (only PDF/DOCX)`);
      else if (f.size > MAX_MB * 1024 * 1024) rejected.push(`${f.name} (>5 MB)`);
      else valid.push(f);
    }
    setFiles(prev => {
      const merged = [...prev, ...valid].slice(0, MAX);
      return merged;
    });
    if (rejected.length) toast.warning(`Skipped: ${rejected.slice(0, 3).join(", ")}${rejected.length > 3 ? ` (+${rejected.length - 3} more)` : ""}`);
  };

  const upload = async (opts = {}) => {
    if (!files.length) return;
    setBusy(true);
    const fd = new FormData();
    files.forEach(f => fd.append("files", f));
    try {
      const url = `/requisitions/${requisitionId}/candidates/bulk-upload${opts.replaceDuplicates ? "?replace_duplicates=true" : ""}`;
      const { data } = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 5 * 60 * 1000, // 5 min — DeepSeek can be slow
      });
      setResult(data);
      if (data.created > 0) {
        const replaced = (data.items || []).filter(it => it.skipped_reason === "replaced_resume").length;
        toast.success(
          replaced > 0
            ? `${data.created - replaced} new · ${replaced} resume${replaced === 1 ? "" : "s"} replaced`
            : `Parsed ${data.created} new candidate${data.created === 1 ? "" : "s"}.`
        );
      }
      onDone?.();
    } catch (e) {
      err(e);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeAll(); }}>
      <DialogContent className="max-w-2xl" data-testid="bulk-upload-modal">
        <DialogHeader><DialogTitle>Bulk upload CVs — AI parse to candidates</DialogTitle></DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragOver ? "border-violet-500 bg-violet-50" : "border-zinc-300 bg-zinc-50"}`}
              data-testid="bulk-dropzone"
            >
              <UploadSimple size={32} className="mx-auto text-zinc-400"/>
              <p className="text-sm text-zinc-600 mt-3"><strong>Drop CVs here</strong> or click to browse</p>
              <p className="text-[11px] text-zinc-500 mt-1">PDF / DOCX · max {MAX} files · 5 MB each</p>
              <input type="file" multiple accept={accept} className="hidden" id="bulk-cv-input"
                onChange={(e) => addFiles(e.target.files)} data-testid="bulk-file-input"/>
              <label htmlFor="bulk-cv-input">
                <Button variant="outline" size="sm" type="button" className="mt-4" asChild>
                  <span className="cursor-pointer"><FileText size={13} className="mr-1.5"/>Browse files</span>
                </Button>
              </label>
            </div>

            {files.length > 0 && (
              <div className="border border-zinc-200 rounded-md max-h-44 overflow-y-auto" data-testid="bulk-queue">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 px-3 py-2 border-b bg-zinc-50">
                  Queue ({files.length}/{MAX})
                </div>
                <ul className="divide-y divide-zinc-100">
                  {files.map((f, i) => (
                    <li key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                      <span className="truncate"><FileText size={12} className="inline mr-1.5 text-zinc-400"/>{f.name}</span>
                      <span className="text-zinc-400 text-[10px]">{(f.size / 1024).toFixed(0)} KB</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {busy && (
              <div className="text-xs text-zinc-600 bg-violet-50 border border-violet-200 rounded p-3 flex items-center gap-2">
                <MagicWand size={14} className="text-violet-600 animate-pulse"/>
                Parsing {files.length} CV{files.length === 1 ? "" : "s"} with AI… this can take up to {Math.ceil(files.length * 2)}s.
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={closeAll} disabled={busy}>Cancel</Button>
              <Button
                onClick={upload}
                disabled={!files.length || busy}
                className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:from-violet-700 hover:to-fuchsia-700"
                data-testid="bulk-upload-start"
              >
                {busy ? "Parsing…" : `Upload & parse ${files.length || ""}`}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3" data-testid="bulk-result">
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                <div className="text-2xl font-bold text-emerald-700">{result.created}</div>
                <div className="text-[10px] uppercase tracking-wider text-emerald-700">Created</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-3">
                <div className="text-2xl font-bold text-amber-700">{result.duplicates}</div>
                <div className="text-[10px] uppercase tracking-wider text-amber-700">Duplicates</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <div className="text-2xl font-bold text-red-700">{result.failed}</div>
                <div className="text-[10px] uppercase tracking-wider text-red-700">Failed</div>
              </div>
              <div className="bg-zinc-50 border border-zinc-200 rounded p-3">
                <div className="text-2xl font-bold text-zinc-700">{result.total}</div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-700">Total</div>
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto border border-zinc-200 rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="text-left px-3 py-2">File</th>
                    <th className="text-left">Status</th>
                    <th className="text-left">Candidate</th>
                    <th className="text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {result.items.map((it, i) => {
                    const isDup = it.skipped_reason === "duplicate_email" || it.skipped_reason === "duplicate_name_phone";
                    const wasReplaced = it.skipped_reason === "replaced_resume";
                    return (
                      <tr key={i} className="border-b border-zinc-100 align-top">
                        <td className="px-3 py-2 max-w-[180px]">
                          <div className="truncate" title={it.filename}>{it.filename}</div>
                        </td>
                        <td className="py-2 whitespace-nowrap">
                          {wasReplaced ? <span className="text-emerald-700">✓ resume replaced</span>
                            : it.ok && !it.skipped_reason ? <span className="text-emerald-700">✓ created</span>
                            : isDup ? <span className="text-amber-700">⚠ duplicate</span>
                            : <span className="text-red-700" title={it.skipped_reason}>✗ {(it.skipped_reason || "failed").slice(0, 30)}</span>}
                        </td>
                        <td className="text-zinc-700 py-2 max-w-[220px]">
                          <div className="truncate">{it.candidate_name || "—"}</div>
                          {isDup && (
                            <>
                              {it.candidate_email && (
                                <div className="text-[10px] text-zinc-500 truncate" title={it.candidate_email}>{it.candidate_email}</div>
                              )}
                              {it.existing_requisition_title && (
                                <div className="text-[10px] text-zinc-400 truncate">for {it.existing_requisition_title}</div>
                              )}
                              {it.matched_on && (
                                <div className="text-[9px] text-amber-600 uppercase tracking-wider">matched on {it.matched_on}</div>
                              )}
                            </>
                          )}
                        </td>
                        <td className="py-2 whitespace-nowrap">
                          {isDup && it.candidate_id && (
                            <a
                              href={`/app/recruitment?candidate=${it.candidate_id}`}
                              className="text-xs text-violet-700 underline underline-offset-2 hover:text-violet-900"
                              target="_blank" rel="noreferrer"
                              data-testid={`bulk-open-existing-${it.candidate_id}`}
                            >
                              Open →
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {result.duplicates > 0 && (
              <div className="border border-amber-200 bg-amber-50 rounded-md p-3 text-xs" data-testid="bulk-replace-hint">
                <div className="font-medium text-amber-900 mb-1">
                  {result.duplicates} CV{result.duplicates === 1 ? " was" : "s were"} skipped as duplicate.
                </div>
                <div className="text-amber-800 mb-2">
                  If these are updated resumes for the same person, replace the resume file on the existing candidate — parsed skills / experience will be refreshed too.
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs border-amber-300 bg-white hover:bg-amber-100"
                  onClick={() => upload({ replaceDuplicates: true })}
                  disabled={busy}
                  data-testid="bulk-replace-duplicates-btn"
                >
                  {busy ? "Replacing…" : `Replace resume for ${result.duplicates} duplicate${result.duplicates === 1 ? "" : "s"}`}
                </Button>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setResult(null); setFiles([]); }}>Upload more</Button>
              <Button onClick={closeAll}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Compact ranked-row candidate card (for the Ranked view) ─────────────
function CandidateRow({ rank, c, onMove, onAfter, selected, onToggleSelect }) {
  const [aiOpen, setAIOpen] = useState(false);
  const score = c.ai_role_match_score;
  const rec = c.ai_parsed?.recommendation;
  return (
    <div className={`border rounded-lg p-3 flex items-center gap-3 hover:shadow-sm transition ${SCORE_BG(score)} ${selected ? "ring-2 ring-violet-400" : ""}`} data-testid={`ranked-row-${c.id}`}>
      {onToggleSelect && (
        <input
          type="checkbox"
          checked={!!selected}
          onChange={onToggleSelect}
          className="cursor-pointer"
          data-testid={`row-select-${c.id}`}
        />
      )}
      <div className="text-xs font-mono text-zinc-400 w-7 text-right">#{rank}</div>
      <div className={`w-14 h-14 rounded-lg ${score == null ? "bg-zinc-100" : ""} flex items-center justify-center text-center shrink-0 ${score != null ? SCORE_BG(score) : ""}`}>
        <div>
          <div className={`text-xl font-bold leading-none ${SCORE_COLOR(score)}`}>{score ?? "—"}</div>
          <div className="text-[9px] uppercase text-zinc-500 tracking-wide">AI</div>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm truncate">{c.name}</span>
          {c.candidate_code && <span className="text-[9px] font-mono bg-zinc-100 px-1.5 py-0.5 rounded text-zinc-600">{c.candidate_code}</span>}
          <Badge className={CAND_STAGE[c.stage]}>{c.stage.replace(/_/g," ")}</Badge>
          {rec && <Badge variant="outline" className={`text-[10px] ${rec === "advance" ? "border-emerald-300 text-emerald-700" : rec === "hold" ? "border-amber-300 text-amber-700" : "border-rose-300 text-rose-700"}`}>{rec}</Badge>}
        </div>
        <div className="text-xs text-zinc-600 mt-0.5 truncate">
          {(c.current_title || c.current_designation || c.current_role) || "—"}
          {c.current_company ? ` · ${c.current_company}` : ""}
          {(c.experience_years ?? c.total_experience_years) != null && ` · ${c.experience_years ?? c.total_experience_years} yrs`}
          {(c.location || c.current_location) && ` · ${c.location || c.current_location}`}
          {c.source && <span className="capitalize text-zinc-500"> · {c.source.replace(/_/g," ")}</span>}
        </div>
        {c.ai_role_match_reasoning && (
          <div className="text-xs text-zinc-500 mt-1 line-clamp-1" title={c.ai_role_match_reasoning}>{c.ai_role_match_reasoning}</div>
        )}
        {c.last_email_sent_at && <EmailStatusBadge cand={c}/>}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAIOpen(true)} data-testid={`ai-detail-${c.id}`}>Details</Button>
        <Select value={c.stage} onValueChange={(v) => onMove(c.id, v)}>
          <SelectTrigger className="h-7 text-xs w-32" data-testid={`stage-row-${c.id}`}><SelectValue/></SelectTrigger>
          <SelectContent>{PIPE_STAGES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g," ")}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {aiOpen && <AIParseDialog cand={c} onClose={() => setAIOpen(false)}/>}
    </div>
  );
}

// ─── Cross-req AI Insights page ──────────────────────────────────────────
function EmailStatusBadge({ cand }) {
  const status = cand.last_email_status || "sent";
  const opens = cand.last_email_open_count || 0;
  const clicks = cand.last_email_click_count || 0;
  const tpl = (cand.last_email_template || "").replace(/_/g, " ");
  const color = status === "bounced" ? "bg-rose-100 text-rose-700"
    : status === "complained" ? "bg-rose-100 text-rose-700"
    : opens > 0 ? "bg-emerald-100 text-emerald-700"
    : "bg-blue-100 text-blue-700";
  return (
    <div className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded mt-1 ${color}`}
         data-testid={`email-status-${cand.id}`}
         title={`Last email: ${tpl} (${status})`}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
      </svg>
      <span className="capitalize">{tpl || "email"} · {status}</span>
      {opens > 0 && <span>· opened {opens}×</span>}
      {clicks > 0 && <span>· clicked</span>}
    </div>
  );
}

export function RecruitmentInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/requisitions/_/insights").then(r => { setData(r.data); setLoading(false); }).catch(e => { err(e); setLoading(false); });
  }, []);
  if (loading) return <AppShell title="AI Insights"><div className="text-sm text-zinc-500 py-10 text-center">Loading…</div></AppShell>;
  if (!data) return <AppShell title="AI Insights"><div className="text-sm text-zinc-500 py-10 text-center">No data yet.</div></AppShell>;

  const maxConv = Math.max(...(data.source_roi || []).map(r => r.conversion), 1);
  const maxApplies = Math.max(...(data.source_roi || []).map(r => r.applies), 1);

  return (
    <AppShell title="AI Insights">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Avg AI score" value={data.req_score_summary?.[0]?.avg_score ?? "—"} hint="Across analyzed candidates"/>
        <StatCard label="Top picks" value={(data.top_picks || []).length} hint="Active high-fit"/>
        <StatCard label="Sources tracked" value={(data.source_roi || []).length} hint="Across all reqs"/>
        <StatCard label="Reasons logged" value={(data.rejection_reasons || []).length} hint="Rejection insights"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top picks */}
        <SectionCard title={<span className="flex items-center gap-2"><Sparkle weight="fill" className="text-violet-600"/>AI top picks</span>}
                     subtitle="Highest-fit active candidates across every open role.">
          <div className="space-y-2">
            {(data.top_picks || []).length === 0 && <div className="text-xs text-zinc-500 py-4">No active candidates ranked yet. Open a requisition and click Run AI Analysis.</div>}
            {(data.top_picks || []).map((p, i) => (
              <div key={p.id} className={`flex items-center gap-3 p-2 rounded border ${SCORE_BG(p.score)}`} data-testid={`insights-pick-${p.id}`}>
                <div className="w-7 text-xs font-mono text-zinc-400 text-right">#{i+1}</div>
                <div className={`w-12 h-12 rounded grid place-items-center ${SCORE_BG(p.score)}`}>
                  <div className={`text-lg font-bold ${SCORE_COLOR(p.score)}`}>{p.score}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{p.name}</div>
                  <div className="text-xs text-zinc-600 truncate">{p.requisition_title}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{p.current_company || "—"} · {p.experience_years || 0} yrs · {p.source || "—"}</div>
                </div>
                <Badge className={CAND_STAGE[p.stage]}>{p.stage.replace(/_/g," ")}</Badge>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Source ROI */}
        <SectionCard title={<span className="flex items-center gap-2"><TrendUp className="text-emerald-600"/>Source ROI</span>}
                     subtitle="Hires-per-applicant, the only metric that matters.">
          <div className="space-y-2">
            {(data.source_roi || []).map(r => (
              <div key={r.source} className="text-xs" data-testid={`source-roi-${r.source}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="capitalize font-medium">{r.source.replace(/_/g," ")}</span>
                  <span className="text-zinc-600 tabular-nums">{r.hires}/{r.applies} · <span className={r.conversion >= 15 ? "text-emerald-700 font-semibold" : r.conversion >= 5 ? "text-blue-700" : "text-zinc-500"}>{r.conversion}%</span></span>
                </div>
                <div className="flex gap-1 h-2 bg-zinc-100 rounded overflow-hidden">
                  <div className="bg-zinc-300" style={{ width: `${(r.applies / maxApplies) * 100}%` }} title="applies"/>
                  <div className="bg-emerald-500" style={{ width: `${(r.conversion / maxConv) * 50}%` }} title="conversion"/>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Req-level AI score summary */}
        <SectionCard title={<span className="flex items-center gap-2"><ChartBar className="text-blue-600"/>Avg AI score per role</span>}
                     subtitle="Healthier average → richer pipeline.">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Role</TableHead><TableHead className="text-right">Candidates</TableHead>
              <TableHead className="text-right">Avg score</TableHead><TableHead className="text-right">Top</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(data.req_score_summary || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-xs text-zinc-500 py-4">Run AI Analysis on at least one role to populate.</TableCell></TableRow>}
              {(data.req_score_summary || []).map(r => (
                <TableRow key={r.requisition_id} data-testid={`insights-req-${r.requisition_id}`}>
                  <TableCell className="text-sm"><Link to={`/app/recruitment/requisitions/${r.requisition_id}`} className="hover:underline font-medium">{r.title}</Link><div className="text-[10px] font-mono text-zinc-400">{r.code}</div></TableCell>
                  <TableCell className="text-right tabular-nums text-xs">{r.candidates}</TableCell>
                  <TableCell className={`text-right tabular-nums text-sm font-semibold ${SCORE_COLOR(r.avg_score)}`}>{r.avg_score}</TableCell>
                  <TableCell className={`text-right tabular-nums text-xs font-semibold ${SCORE_COLOR(r.top_score)}`}>{r.top_score}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>

        {/* Rejection reasons + BGV */}
        <SectionCard title={<span className="flex items-center gap-2"><Funnel className="text-rose-600"/>Why candidates got rejected</span>}
                     subtitle="Patterns from past rejection reasons.">
          <div className="space-y-2 mb-4">
            {(data.rejection_reasons || []).length === 0 && <div className="text-xs text-zinc-500">No rejection reasons logged.</div>}
            {(data.rejection_reasons || []).map(r => (
              <div key={r.reason} className="flex items-center justify-between text-xs border-b border-zinc-100 pb-1">
                <span className="truncate flex-1">{r.reason}</span>
                <Badge variant="outline" className="ml-2">{r.count}</Badge>
              </div>
            ))}
          </div>
          <div className="pt-3 border-t border-zinc-100">
            <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">Background check status</div>
            <div className="flex flex-wrap gap-2">
              {(data.bgv_summary || []).map(b => (
                <span key={b.status} className={`text-xs px-2 py-0.5 rounded ${b.status === "clear" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : b.status === "in_progress" ? "bg-amber-50 text-amber-700 border border-amber-200" : b.status === "red" ? "bg-rose-50 text-rose-700 border border-rose-200" : "bg-zinc-100 text-zinc-600 border border-zinc-200"}`}>
                  {b.status.replace(/_/g," ")} · {b.count}
                </span>
              ))}
            </div>
          </div>
        </SectionCard>
      </div>
    </AppShell>
  );
}

// ─── Offers tab ───────────────────────────────────────────────────────────
export function Offers() {
  const [rows, setRows] = useState([]);
  const [cands, setCands] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ candidate_id:"", job_title:"", doj:"", annual_ctc:"", currency:"INR", work_mode:"wfo", employment_type:"full_time", location:"", letter_template_id:"" });

  const load = async () => {
    try {
      const [o, c, t] = await Promise.all([api.get("/offers"), api.get("/candidates"), api.get("/letter-templates").catch(()=>({data:[]}))]);
      setRows(o.data); setCands(c.data); setTemplates(t.data);
    } catch (e) { err(e); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = { ...f, annual_ctc: Number(f.annual_ctc || 0) };
      if (!body.letter_template_id) delete body.letter_template_id;
      if (!body.location) delete body.location;
      await api.post("/offers", body); toast.success("Offer drafted");
      setOpen(false); setF({ candidate_id:"", job_title:"", doj:"", annual_ctc:"", currency:"INR", work_mode:"wfo", employment_type:"full_time", location:"", letter_template_id:"" });
      load();
    } catch (e) { err(e); }
  };
  const send = async (id) => { try { await api.post(`/offers/${id}/send`); toast.success("Offer sent"); load(); } catch (e) { err(e); } };
  const decide = async (id, decision) => {
    const reason = decision === "decline" ? (prompt("Reason for decline?") || "") : undefined;
    try { await api.post(`/offers/${id}/decision`, { decision, reason }); toast.success(`Offer ${decision}ed`); load(); } catch (e) { err(e); }
  };
  const convert = async (cand_id) => {
    if (!window.confirm("Create Employee record + start onboarding?")) return;
    try {
      await api.post(`/candidates/${cand_id}/convert-to-employee`, { auto_start_onboarding: true });
      toast.success("Converted to employee, onboarding started"); load();
    } catch (e) { err(e); }
  };

  const sentCands = cands.filter(c => ["shortlisted","interview","offer_pending"].includes(c.stage));
  return (
    <AppShell title="Offers">
      <SectionCard title="Offers" subtitle="Draft → Send → Accepted/Declined → Hire."
        action={<Button size="sm" onClick={()=>setOpen(true)} data-testid="new-offer-btn" className="gap-1.5"><PaperPlaneTilt size={14}/> New offer</Button>}
        testid="section-offers">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Candidate</TableHead><TableHead>Job title</TableHead>
            <TableHead className="text-right">CTC</TableHead><TableHead>DOJ</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-zinc-500">No offers yet.</TableCell></TableRow>}
            {rows.map(o => (
              <TableRow key={o.id} data-testid={`offer-row-${o.id}`}>
                <TableCell className="font-medium">{o.candidate_name}<div className="text-xs text-zinc-500">{o.requisition_title}</div></TableCell>
                <TableCell>{o.job_title}</TableCell>
                <TableCell className="text-right tabular-nums">{o.currency} {Number(o.annual_ctc).toLocaleString("en-IN")}</TableCell>
                <TableCell>{o.doj}</TableCell>
                <TableCell><Badge className={OFFER_STATUS[o.status]}>{o.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end flex-wrap">
                    {o.status === "draft" && <Button size="sm" onClick={()=>send(o.id)} data-testid={`offer-send-${o.id}`} className="gap-1"><PaperPlaneTilt size={12}/> Send</Button>}
                    {o.status === "sent" && <><Button size="sm" onClick={()=>decide(o.id,"accept")} data-testid={`offer-accept-${o.id}`}>Accepted</Button><Button size="sm" variant="outline" className="text-red-600" onClick={()=>decide(o.id,"decline")}>Declined</Button></>}
                    {o.status === "accepted" && <Button size="sm" variant="secondary" className="gap-1" onClick={()=>convert(o.candidate_id)} data-testid={`offer-convert-${o.id}`}><CheckCircle size={12}/> Convert</Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="new-offer-dialog">
          <DialogHeader><DialogTitle>New offer</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Candidate</Label>
              <Select value={f.candidate_id} onValueChange={v=>setF({...f,candidate_id:v})}>
                <SelectTrigger data-testid="offer-cand"><SelectValue placeholder="Pick candidate"/></SelectTrigger>
                <SelectContent className="max-h-72">{sentCands.map(c => <SelectItem key={c.id} value={c.id}>{c.name} — {c.requisition_title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Job title</Label><Input value={f.job_title} onChange={e=>setF({...f,job_title:e.target.value})} data-testid="offer-title"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Proposed DOJ</Label><DateInput value={f.doj} onChange={e=>setF({...f,doj:e.target.value})} data-testid="offer-doj"/></div>
              <div><Label>Annual CTC</Label><Input type="number" value={f.annual_ctc} onChange={e=>setF({...f,annual_ctc:e.target.value})} data-testid="offer-ctc"/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Work mode</Label>
                <Select value={f.work_mode} onValueChange={v=>setF({...f,work_mode:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="wfo">Office</SelectItem><SelectItem value="wfh">Remote</SelectItem><SelectItem value="hybrid">Hybrid</SelectItem><SelectItem value="field">Field</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Location</Label><Input value={f.location} onChange={e=>setF({...f,location:e.target.value})}/></div>
            </div>
            <div><Label>Offer letter template (optional)</Label>
              <Select value={f.letter_template_id || "__none__"} onValueChange={v=>setF({...f,letter_template_id: v==="__none__"?"":v})}>
                <SelectTrigger><SelectValue placeholder="None"/></SelectTrigger>
                <SelectContent><SelectItem value="__none__">None</SelectItem>{templates.filter(t=>t.category==="offer").map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-1">When selected, letter auto-generates with merge fields (candidate_name, job_title, annual_ctc, doj, etc.)</p>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="offer-save">Draft offer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

// ─── Candidate card (rich) ──────────────────────────────────────────────
const BG_BADGE = {
  not_started: "bg-zinc-100 text-zinc-600",
  in_progress: "bg-amber-100 text-amber-700",
  clear: "bg-emerald-100 text-emerald-700",
  amber: "bg-orange-100 text-orange-700",
  red: "bg-rose-100 text-rose-700",
  cancelled: "bg-zinc-100 text-zinc-500",
};

function CandidateCard({ c, stages, onMove, onAfter }) {
  const [busy, setBusy] = useState(false);
  const [parsedView, setParsedView] = useState(null); // candidate object when dialog open
  const runAIParse = async (e) => {
    e?.stopPropagation();
    setBusy(true);
    try {
      const { data } = await api.post(`/candidates/${c.id}/ai-parse`);
      toast.success(`AI: ${data.ai_role_match_score || 0}% match`);
      setParsedView(data);
      onAfter?.();
    } catch (err2) {
      toast.error(err2?.response?.data?.detail || "AI parse failed");
    } finally {
      setBusy(false);
    }
  };
  const viewParsed = (e) => {
    e?.stopPropagation();
    setParsedView(c);
  };
  const triggerBG = async (e) => {
    e?.stopPropagation();
    try {
      await api.post(`/bg-check/candidates/${c.id}/trigger`, {});
      toast.success("BG check triggered");
      onAfter?.();
    } catch (err2) {
      if (err2?.response?.status === 402) {
        toast.error("Background Verification module not enabled. Settings → Modules.");
      } else {
        toast.error(err2?.response?.data?.detail || "BG check failed");
      }
    }
  };
  const score = c.ai_role_match_score;
  const scoreColor = score == null ? "" : score >= 75 ? "text-emerald-700" : score >= 50 ? "text-amber-700" : "text-rose-700";
  return (
    <div className="bg-white rounded-md border border-zinc-200 p-2 text-xs hover:border-teal-300 transition cursor-default" data-testid={`cand-${c.id}`}>
      <div className="flex items-start justify-between gap-1">
        <div className="font-medium text-zinc-900 truncate flex-1">{c.name}</div>
        {score != null && (
          <span className={`text-[10px] font-bold ${scoreColor}`} title={c.ai_role_match_reasoning || "AI role-match"}>
            {score}%
          </span>
        )}
      </div>
      <div className="text-zinc-500 truncate text-[11px]">{c.email}</div>
      <div className="flex flex-wrap gap-1 mt-1.5">
        {c.candidate_code && <span className="text-[9px] bg-zinc-100 text-zinc-700 px-1.5 py-0.5 rounded font-mono" data-testid={`cand-code-${c.id}`}>{c.candidate_code}</span>}
        {c.reserved_employee_code && <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-mono" title="EMP-ID reserved" data-testid={`reserved-${c.id}`}>{c.reserved_employee_code}</span>}
        {c.bg_check_status && c.bg_check_status !== "not_started" && (
          <span className={`text-[9px] px-1.5 py-0.5 rounded ${BG_BADGE[c.bg_check_status] || BG_BADGE.not_started}`} data-testid={`bg-badge-${c.id}`}>
            BG: {c.bg_check_status}
          </span>
        )}
      </div>
      {c.expected_ctc && <div className="text-zinc-500 mt-1 text-[11px]">Expected: ₹{Number(c.expected_ctc).toLocaleString("en-IN")}</div>}
      <div className="flex items-center gap-1 mt-1.5">
        <button
          type="button"
          onClick={runAIParse}
          disabled={busy || !c.resume_filename}
          className="text-[10px] px-1.5 py-0.5 bg-violet-50 text-violet-700 rounded border border-violet-200 hover:bg-violet-100 disabled:opacity-40 disabled:cursor-not-allowed"
          title={c.resume_filename ? "Parse CV with AI" : "Attach a resume first"}
          data-testid={`ai-parse-${c.id}`}
        >
          {busy ? "…" : "AI parse"}
        </button>
        {c.ai_parsed && (
          <button
            type="button"
            onClick={viewParsed}
            className="text-[10px] px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded border border-indigo-200 hover:bg-indigo-100"
            title="View extracted data"
            data-testid={`ai-view-${c.id}`}
          >
            View
          </button>
        )}
        {(!c.bg_check_status || c.bg_check_status === "not_started") && (
          <button
            type="button"
            onClick={triggerBG}
            className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded border border-amber-200 hover:bg-amber-100"
            title="Trigger background check"
            data-testid={`bg-trigger-${c.id}`}
          >
            BG check
          </button>
        )}
      </div>
      <Select value={c.stage} onValueChange={(v) => onMove(c.id, v)}>
        <SelectTrigger className="h-7 text-xs mt-2" data-testid={`stage-${c.id}`}><SelectValue/></SelectTrigger>
        <SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
      </Select>
      {parsedView && <AIParseDialog cand={parsedView} onClose={() => setParsedView(null)} />}
    </div>
  );
}

function AIParseDialog({ cand, onClose }) {
  const [showComposer, setShowComposer] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showCommittee, setShowCommittee] = useState(false);
  // ai_parsed is a BOOLEAN flag on the row (not a payload). The actual
  // parsed fields live at the top level of `cand` (current_designation,
  // total_experience_years, skills, summary, education, etc).
  // Older flows nested them under `ai_parsed_payload`; keep that as a
  // belt-and-braces fallback so legacy rows still render.
  const p = (typeof cand.ai_parsed === "object" && cand.ai_parsed) ||
            cand.ai_parsed_payload || {};
  const fld = (...keys) => {
    for (const k of keys) {
      const v = cand[k] ?? p[k];
      if (v !== undefined && v !== null && v !== "") return v;
    }
    return null;
  };
  const summary = fld("summary");
  const currentTitle = fld("current_title", "current_designation", "current_role");
  const currentCompany = fld("current_company");
  const expYears = fld("total_experience_years", "experience_years");
  const currentCtc = fld("current_ctc");
  const expectedCtc = fld("expected_ctc");
  const noticeDays = fld("notice_period_days", "notice_period");
  const skills = (cand.skills && cand.skills.length ? cand.skills : null) || p.skills || [];
  const education = (cand.education && cand.education.length ? cand.education : null) || p.education || [];
  const location = fld("current_location", "preferred_location");
  const score = cand.ai_role_match_score;
  const scoreColor = score == null ? "text-zinc-500"
    : score >= 75 ? "text-emerald-700"
    : score >= 50 ? "text-amber-700"
    : "text-rose-700";
  const fmtINR = (v) => v == null ? "—" : `₹${Number(v).toLocaleString("en-IN")}`;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-parse-dialog">
        <DialogHeader>
          <DialogTitle>{cand.name}</DialogTitle>
          <div className="text-xs text-zinc-500">{cand.email}{cand.phone ? ` · ${cand.phone}` : ""}{location ? ` · ${location}` : ""}</div>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-3 bg-violet-50 border border-violet-200 rounded p-3">
            <div className={`text-3xl font-bold ${scoreColor}`} data-testid="ai-score-big">{score ?? "—"}%</div>
            <div className="flex-1">
              <div className="text-xs text-zinc-500">AI role-match score</div>
              <div className="text-sm">
                {cand.ai_role_match_reasoning || p.match_reasoning || (score == null
                  ? "Click \"Run AI Analysis\" on the role to score this candidate against the JD."
                  : "No reasoning provided.")}
              </div>
            </div>
          </div>

          {summary && (
            <div>
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Summary</div>
              <div className="text-sm mt-1 whitespace-pre-wrap leading-relaxed">{summary}</div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Current role</div>
              <div className="mt-1">{currentTitle || "—"}</div>
              {currentCompany && <div className="text-zinc-500 text-xs">{currentCompany}</div>}
            </div>
            <div>
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Experience</div>
              <div className="mt-1">{expYears != null ? `${expYears} years` : "—"}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Current CTC</div>
              <div className="mt-1">{fmtINR(currentCtc)}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Expected CTC</div>
              <div className="mt-1">{fmtINR(expectedCtc)}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Notice period</div>
              <div className="mt-1">{noticeDays != null ? `${noticeDays} days` : "—"}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Source</div>
              <div className="mt-1 capitalize">{cand.source || "—"}</div>
            </div>
          </div>

          {skills.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">Skills extracted</div>
              <div className="flex flex-wrap gap-1.5" data-testid="ai-skills">
                {skills.map((s, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 bg-zinc-100 text-zinc-700 rounded">{s}</span>
                ))}
              </div>
            </div>
          )}

          {education.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">Education</div>
              <div className="space-y-1">
                {education.map((e, i) => (
                  <div key={i} className="text-xs text-zinc-700">
                    {e.degree || "—"}{e.institution ? ` · ${e.institution}` : ""}{e.year ? ` (${e.year})` : ""}
                  </div>
                ))}
              </div>
            </div>
          )}

          {cand.resume_s3_key && (
            <div className="pt-2 border-t border-zinc-100">
              <a
                href={`${API}/candidates/${cand.id}/resume`}
                target="_blank" rel="noreferrer"
                className="text-xs text-violet-700 hover:text-violet-900 underline"
                data-testid="ai-resume-link"
              >
                ↓ Download original resume ({cand.resume_filename || "CV"})
              </a>
            </div>
          )}
        </div>
        <DialogFooter className="flex-row justify-between flex-wrap gap-2">
          <div className="flex gap-2 flex-wrap">
            <Button variant="default" onClick={() => setShowComposer(true)} data-testid="open-email-composer">
              ✉ Send email
            </Button>
            <Button variant="default" className="bg-violet-700 hover:bg-violet-800" onClick={() => setShowSchedule(true)} data-testid="open-schedule-round">
              📅 Schedule interview round
            </Button>
            <Button variant="default" className="bg-amber-600 hover:bg-amber-700" onClick={() => setShowCommittee(true)} data-testid="open-hiring-committee">
              ⚖ Hiring committee
            </Button>
          </div>
          <Button variant="outline" onClick={onClose} data-testid="ai-parse-close">Close</Button>
        </DialogFooter>
      </DialogContent>
      {showComposer && (
        <EmailComposer cand={cand} onClose={() => setShowComposer(false)} />
      )}
      {showSchedule && (
        <ScheduleRoundDialog cand={cand} onClose={() => setShowSchedule(false)} onScheduled={() => setShowSchedule(false)} />
      )}
      {showCommittee && (
        <HiringCommitteeDialog cand={cand} onClose={() => setShowCommittee(false)} onDecided={() => setShowCommittee(false)} />
      )}
    </Dialog>
  );
}

function EmailComposer({ cand, onClose }) {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [interview, setInterview] = useState({ date: "", time: "", mode: "Video call", location: "" });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/recruitment/email-templates").then(({ data }) => {
      if (!alive) return;
      setTemplates(data);
      // Auto-select based on current stage if a template hints at it
      const stage = (cand.stage || "").toLowerCase();
      const match = data.find(t => (t.stage_hint || "").toLowerCase() === stage);
      const pick = match || data.find(t => t.key === "shortlisted") || data[0];
      if (pick) {
        setSelectedKey(pick.key);
        setSubject(pick.subject);
        setBody(pick.body);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
    return () => { alive = false; };
  }, [cand]);

  const onTemplateChange = (k) => {
    setSelectedKey(k);
    const t = templates.find(t => t.key === k);
    if (t) { setSubject(t.subject); setBody(t.body); }
  };

  const showInterviewFields = selectedKey === "interview";

  // Preview with merge substitutions (mirrors backend `_merge` behaviour).
  const previewCtx = {
    candidate_name: cand.name || "",
    candidate_first_name: (cand.name || "").split(" ")[0] || "",
    candidate_email: cand.email || "",
    role_title: cand.requisition_title || "the role",
    company_name: user?.company_name || "Your company",
    recruiter_name: user?.name || "You",
    interview_date: interview.date || "{interview_date}",
    interview_time: interview.time || "{interview_time}",
    interview_mode: interview.mode || "{interview_mode}",
    interview_location: interview.location || "{interview_location}",
  };
  const sub = (s) => (s || "").replace(/\{([a-z_][a-z0-9_]*)\}/g, (m, k) => previewCtx[k] != null && previewCtx[k] !== "" ? previewCtx[k] : m);

  const send = async () => {
    if (sending) return;
    setSending(true);
    try {
      const payload = { template_key: selectedKey, subject, body };
      if (showInterviewFields) payload.interview = interview;
      const { data } = await api.post(`/recruitment/candidates/${cand.id}/send-email`, payload);
      setResult(data);
      toast.success(`Email sent to ${data.candidate_email}`, { duration: 6000 });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (result) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-md" data-testid="email-sent-card">
          <DialogHeader><DialogTitle>Email sent ✓</DialogTitle></DialogHeader>
          <div className="py-6 text-center space-y-2">
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
            <div className="text-sm">Delivered to <strong>{result.candidate_email}</strong></div>
            <div className="text-xs text-zinc-500">Reply-To header set to your email — the candidate's response will land in your inbox.</div>
          </div>
          <DialogFooter><Button onClick={onClose}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="email-composer">
        <DialogHeader>
          <DialogTitle>Send email to {cand.name}</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500 mt-0.5">{cand.email}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-sm text-zinc-500">Loading templates…</div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Template</Label>
              <select
                value={selectedKey}
                onChange={(e) => onTemplateChange(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-zinc-300 rounded text-sm"
                data-testid="email-template-picker"
              >
                {templates.map(t => (
                  <option key={t.key} value={t.key}>{t.name}</option>
                ))}
              </select>
            </div>

            {showInterviewFields && (
              <div className="grid grid-cols-2 gap-2 p-3 bg-amber-50 border border-amber-200 rounded">
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input value={interview.date} onChange={(e) => setInterview(p => ({ ...p, date: e.target.value }))} placeholder="Mon 9 Jun 2026" data-testid="email-iv-date"/>
                </div>
                <div>
                  <Label className="text-xs">Time</Label>
                  <Input value={interview.time} onChange={(e) => setInterview(p => ({ ...p, time: e.target.value }))} placeholder="10:30 AM IST" data-testid="email-iv-time"/>
                </div>
                <div>
                  <Label className="text-xs">Mode</Label>
                  <select
                    value={interview.mode}
                    onChange={(e) => setInterview(p => ({ ...p, mode: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border border-zinc-300 rounded text-sm bg-white"
                    data-testid="email-iv-mode"
                  >
                    <option>Video call</option>
                    <option>In-person</option>
                    <option>Phone</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Location / Link</Label>
                  <Input value={interview.location} onChange={(e) => setInterview(p => ({ ...p, location: e.target.value }))} placeholder="Meet link or address" data-testid="email-iv-loc"/>
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject"/>
              <div className="text-[11px] text-zinc-500 mt-1">Preview: <strong>{sub(subject)}</strong></div>
            </div>

            <div>
              <Label className="text-xs">Body (Markdown — use **bold**, blank line for paragraph)</Label>
              <Textarea
                rows={10} value={body}
                onChange={(e) => setBody(e.target.value)}
                className="font-mono text-sm"
                data-testid="email-body"
              />
            </div>

            <div className="border border-zinc-200 rounded p-3 bg-zinc-50">
              <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1">Preview (rendered)</div>
              <div className="text-sm whitespace-pre-wrap text-zinc-700 leading-relaxed">
                {sub(body)}
              </div>
            </div>

            <div className="text-[11px] text-zinc-500">
              Available merge fields: <code>{"{candidate_name} {role_title} {company_name} {recruiter_name}"}</code>
              {showInterviewFields && <> <code>{"{interview_date} {interview_time} {interview_mode} {interview_location}"}</code></>}
            </div>
          </div>
        )}
        <DialogFooter>
          {sending && <div className="text-xs text-zinc-500 mr-auto flex items-center gap-2"><span className="inline-block h-3 w-3 rounded-full border-2 border-zinc-300 border-t-zinc-900 animate-spin"></span>Sending…</div>}
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button onClick={send} disabled={sending || loading || !selectedKey} data-testid="email-send-btn">
            {sending ? "Sending…" : `Send to ${cand.email}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// EditRequisitionDialog — edit a live requisition. Backend already supports
// PATCH /api/requisitions/{rid} for all fields. Warns on AI-impacting edits;
// shows last-edited audit trail in the footer.
// ---------------------------------------------------------------------------
const EMPLOYMENT_TYPES = ["full_time", "part_time", "contract", "internship", "consultant"];
const WORK_MODES = ["wfo", "remote", "hybrid", "field"];
const PRIORITIES = ["low", "medium", "high", "urgent"];
const SCORING_FIELDS = new Set(["skills", "experience_min", "experience_max", "location", "work_mode", "employment_type", "requirements"]);

function EditRequisitionDialog({ req, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    title: req.title || "",
    department: req.department || "",
    location: req.location || "",
    employment_type: req.employment_type || "full_time",
    work_mode: req.work_mode || "wfo",
    openings: req.openings || 1,
    priority: req.priority || "medium",
    experience_min: req.experience_min ?? "",
    experience_max: req.experience_max ?? "",
    salary_min: req.salary_min ?? "",
    salary_max: req.salary_max ?? "",
    currency: req.currency || "INR",
    description_markdown: req.description_markdown || "",
    responsibilities: req.responsibilities || [],
    requirements: req.requirements || [],
    skills: req.skills || [],
    benefits: req.benefits || [],
    is_public: req.is_public ?? true,
    target_close_date: req.target_close_date || "",
  }));
  const [saving, setSaving] = useState(false);
  const [smart, setSmart] = useState({ loading: false, suggestions: null, health: null, hints: null, lastDescHash: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runSmartFill = async () => {
    if ((form.description_markdown || "").trim().length < 100) {
      toast.error("Add at least 100 chars to the description first.");
      return;
    }
    setSmart((s) => ({ ...s, loading: true }));
    try {
      const r = await api.post(`/requisitions/extract-jd`, {
        title: form.title,
        description: form.description_markdown,
        existing: {
          skills: form.skills, responsibilities: form.responsibilities,
          requirements: form.requirements, benefits: form.benefits,
        },
      });
      setSmart({
        loading: false,
        suggestions: r.data.suggestions,
        health: r.data.health,
        hints: r.data.hints,
        lastDescHash: form.description_markdown,
      });
      const total = Object.values(r.data.suggestions || {}).reduce((a, b) => a + (b?.length || 0), 0);
      toast.success(`AI suggested ${total} item${total === 1 ? "" : "s"} across 4 columns`);
    } catch (e) { err(e); setSmart((s) => ({ ...s, loading: false })); }
  };
  const stale = smart.suggestions && smart.lastDescHash !== form.description_markdown;
  const promote = (column, value) => {
    set(column, [...(form[column] || []), value]);
    setSmart((s) => ({
      ...s,
      suggestions: { ...s.suggestions, [column]: (s.suggestions[column] || []).filter((x) => x !== value) },
    }));
  };
  const dismissOne = (column, value) => {
    setSmart((s) => ({
      ...s,
      suggestions: { ...s.suggestions, [column]: (s.suggestions[column] || []).filter((x) => x !== value) },
    }));
  };
  const addAll = (column) => {
    const toAdd = smart.suggestions[column] || [];
    if (!toAdd.length) return;
    set(column, [...(form[column] || []), ...toAdd]);
    setSmart((s) => ({ ...s, suggestions: { ...s.suggestions, [column]: [] } }));
  };
  const applyHints = () => {
    const next = { ...form };
    if (smart.hints?.department && !next.department) next.department = smart.hints.department;
    if (smart.hints?.experience_min != null && next.experience_min === "") next.experience_min = smart.hints.experience_min;
    if (smart.hints?.experience_max != null && next.experience_max === "") next.experience_max = smart.hints.experience_max;
    setForm(next);
    toast.success("Filled empty fields from JD");
  };

  const isOpen = req.status === "open";
  const dirtyScoringField = useMemo(() => {
    const changed = [];
    for (const k of Object.keys(form)) {
      const before = req[k];
      const after = form[k];
      const same = JSON.stringify(before ?? "") === JSON.stringify(after ?? "");
      if (!same && SCORING_FIELDS.has(k)) changed.push(k);
    }
    return changed;
  }, [form, req]);

  const save = async () => {
    if (Number(form.openings) < (req.openings_filled || 0)) {
      toast.error(`Openings can't go below ${req.openings_filled} (already filled).`);
      return;
    }
    if (dirtyScoringField.length > 0 && isOpen) {
      if (!window.confirm(`You're changing ${dirtyScoringField.join(", ")}. Existing AI candidate scores will be recomputed in the background. Continue?`)) {
        return;
      }
    }
    const body = {
      ...form,
      experience_min: form.experience_min === "" ? null : Number(form.experience_min),
      experience_max: form.experience_max === "" ? null : Number(form.experience_max),
      salary_min: form.salary_min === "" ? null : Number(form.salary_min),
      salary_max: form.salary_max === "" ? null : Number(form.salary_max),
      openings: Number(form.openings) || 1,
      target_close_date: form.target_close_date || null,
    };
    setSaving(true);
    try {
      const r = await api.patch(`/requisitions/${req.id}`, body);
      const changed = r.data?._changes_count || 0;
      const scoring = r.data?._scoring_impact;
      if (changed === 0) toast("No changes to save.");
      else toast.success(`${changed} field${changed === 1 ? "" : "s"} updated${scoring ? " · AI re-scoring in background" : ""}`);
      onSaved(r.data);
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-3xl" data-testid="edit-req-dialog">
        <DialogHeader>
          <DialogTitle>Edit &middot; {req.code}</DialogTitle>
          <DialogDescription>
            Updates a live requisition. Code <code className="px-1 py-0.5 bg-zinc-100 rounded">{req.code}</code> is permanent.
            {req.last_edited_at && (
              <span className="block mt-1 text-[11px] text-zinc-500">
                Last edited {new Date(req.last_edited_at).toLocaleString()} by {req.last_edited_by_name || "&mdash;"}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[68vh] overflow-y-auto pr-1 space-y-4 py-2">
          {dirtyScoringField.length > 0 && isOpen && (
            <div className="text-xs px-3 py-2 rounded border border-amber-300 bg-amber-50 text-amber-900 flex items-start gap-2">
              <Warning size={14} className="mt-0.5 shrink-0" />
              <span>You&apos;re changing <strong>{dirtyScoringField.join(", ")}</strong> &mdash; AI candidate scores will be recomputed in the background after save.</span>
            </div>
          )}

          {/* ---- Smart Fill panel ---- */}
          <div className="rounded-md border border-indigo-200 bg-gradient-to-br from-indigo-50 to-violet-50 px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-indigo-900 flex items-center gap-2">
              <Sparkle size={14} weight="fill" className="text-indigo-600"/>
              <span><strong>Smart Fill from JD</strong> &mdash; DeepSeek scans your description and suggests skills, responsibilities, requirements, benefits. You pick what&apos;s relevant.</span>
            </div>
            <Button size="sm" onClick={runSmartFill} disabled={smart.loading} className="bg-indigo-600 hover:bg-indigo-700 gap-1.5" data-testid="smart-fill-btn">
              <Sparkle size={13} weight={smart.loading ? "regular" : "fill"} />
              {smart.loading ? "Parsing…" : smart.suggestions ? "Re-run" : "Smart fill from JD"}
            </Button>
          </div>
          {smart.health && (smart.health.missing?.length || smart.health.strengths?.length) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              {smart.health.strengths?.length > 0 && (
                <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2">
                  <div className="font-semibold text-emerald-900 text-[11px] uppercase tracking-wider">JD strengths</div>
                  <ul className="mt-1 list-disc list-inside text-emerald-800">{smart.health.strengths.slice(0,4).map((x,i)=><li key={i}>{x}</li>)}</ul>
                </div>
              )}
              {smart.health.missing?.length > 0 && (
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2">
                  <div className="font-semibold text-amber-900 text-[11px] uppercase tracking-wider">Consider adding</div>
                  <ul className="mt-1 list-disc list-inside text-amber-900">{smart.health.missing.slice(0,4).map((x,i)=><li key={i}>{x}</li>)}</ul>
                </div>
              )}
            </div>
          )}
          {smart.hints && (smart.hints.department || smart.hints.experience_min != null || smart.hints.experience_max != null) && (
            <div className="rounded border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs flex items-center justify-between flex-wrap gap-2">
              <div className="text-zinc-700">
                AI also detected:&nbsp;
                {smart.hints.department && <Badge variant="outline" className="mr-1">Dept: {smart.hints.department}</Badge>}
                {(smart.hints.experience_min != null || smart.hints.experience_max != null) && (
                  <Badge variant="outline">Exp: {smart.hints.experience_min ?? "?"}&ndash;{smart.hints.experience_max ?? "?"} yrs</Badge>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={applyHints} data-testid="apply-hints-btn">Apply to empty fields</Button>
            </div>
          )}
          {stale && (
            <div className="text-[11px] text-amber-700 italic">Description changed — re-run Smart Fill for fresh suggestions.</div>
          )}

          <div>
            <Label className="text-xs">Title</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="edit-title" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Department</Label>
              <Input value={form.department} onChange={(e) => set("department", e.target.value)} data-testid="edit-department" />
            </div>
            <div>
              <Label className="text-xs">Location</Label>
              <Input value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="edit-location" />
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="edit-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Employment type</Label>
              <Select value={form.employment_type} onValueChange={(v) => set("employment_type", v)}>
                <SelectTrigger data-testid="edit-emp-type"><SelectValue /></SelectTrigger>
                <SelectContent>{EMPLOYMENT_TYPES.map((p) => <SelectItem key={p} value={p}>{p.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Work mode</Label>
              <Select value={form.work_mode} onValueChange={(v) => set("work_mode", v)}>
                <SelectTrigger data-testid="edit-work-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{WORK_MODES.map((p) => <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Openings <span className="text-zinc-400">({req.openings_filled || 0} filled)</span></Label>
              <Input type="number" min={req.openings_filled || 0} value={form.openings} onChange={(e) => set("openings", e.target.value)} data-testid="edit-openings" />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Exp min (yrs)</Label>
              <Input type="number" min={0} step={0.5} value={form.experience_min} onChange={(e) => set("experience_min", e.target.value)} data-testid="edit-exp-min" />
            </div>
            <div>
              <Label className="text-xs">Exp max (yrs)</Label>
              <Input type="number" min={0} step={0.5} value={form.experience_max} onChange={(e) => set("experience_max", e.target.value)} data-testid="edit-exp-max" />
            </div>
            <div>
              <Label className="text-xs">Salary min</Label>
              <Input type="number" min={0} value={form.salary_min} onChange={(e) => set("salary_min", e.target.value)} data-testid="edit-salary-min" />
            </div>
            <div>
              <Label className="text-xs">Salary max</Label>
              <Input type="number" min={0} value={form.salary_max} onChange={(e) => set("salary_max", e.target.value)} data-testid="edit-salary-max" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Description (markdown supported)</Label>
            <Textarea rows={6} value={form.description_markdown} onChange={(e) => set("description_markdown", e.target.value)} data-testid="edit-desc" />
          </div>

          <ChipListEditor label="Required skills" items={form.skills} onChange={(v) => set("skills", v)} testid="skills" placeholder="Add a skill and press Enter"
            suggestions={smart.suggestions?.skills} onPromote={(v) => promote("skills", v)} onDismiss={(v) => dismissOne("skills", v)} onAddAll={() => addAll("skills")} />
          <ChipListEditor label="Responsibilities" items={form.responsibilities} onChange={(v) => set("responsibilities", v)} testid="resp" placeholder="Add a responsibility and press Enter"
            suggestions={smart.suggestions?.responsibilities} onPromote={(v) => promote("responsibilities", v)} onDismiss={(v) => dismissOne("responsibilities", v)} onAddAll={() => addAll("responsibilities")} />
          <ChipListEditor label="Requirements" items={form.requirements} onChange={(v) => set("requirements", v)} testid="req-list" placeholder="Add a requirement and press Enter"
            suggestions={smart.suggestions?.requirements} onPromote={(v) => promote("requirements", v)} onDismiss={(v) => dismissOne("requirements", v)} onAddAll={() => addAll("requirements")} />
          <ChipListEditor label="Benefits" items={form.benefits} onChange={(v) => set("benefits", v)} testid="benefits" placeholder="e.g. Health insurance, ESOPs"
            suggestions={smart.suggestions?.benefits} onPromote={(v) => promote("benefits", v)} onDismiss={(v) => dismissOne("benefits", v)} onAddAll={() => addAll("benefits")} />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Target close date (optional)</Label>
              <DateInput value={form.target_close_date ? form.target_close_date.slice(0, 10) : ""} onChange={(e) => set("target_close_date", e.target.value)} data-testid="edit-close-date" />
            </div>
            <label className="flex items-end gap-2 cursor-pointer text-xs pb-1">
              <input type="checkbox" checked={form.is_public} onChange={(e) => set("is_public", e.target.checked)} data-testid="edit-is-public" />
              Listed on public /careers page
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="edit-save">
            {saving ? "Saving&hellip;" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChipListEditor({ label, items, onChange, testid, placeholder, suggestions, onPromote, onDismiss, onAddAll }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.includes(v)) { setDraft(""); return; }
    onChange([...items, v]);
    setDraft("");
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const hasSuggestions = Array.isArray(suggestions) && suggestions.length > 0;
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-xs">{label}</Label>
        {hasSuggestions && (
          <button type="button" onClick={onAddAll} className="text-[11px] text-indigo-600 hover:underline" data-testid={`edit-${testid}-add-all`}>
            <Sparkle size={11} weight="fill" className="inline mr-0.5"/> Add all {suggestions.length}
          </button>
        )}
      </div>
      <div className="flex gap-2 mt-1">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); }
            else if (e.key === "Backspace" && !draft && items.length) onChange(items.slice(0, -1));
          }}
          placeholder={placeholder}
          data-testid={`edit-${testid}-input`}
        />
        <Button type="button" variant="outline" onClick={add} data-testid={`edit-${testid}-add`}>Add</Button>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {items.map((it, i) => (
            <span key={`${it}-${i}`} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-indigo-50 text-indigo-800 border border-indigo-200 rounded">
              {it}
              <button onClick={() => remove(i)} className="hover:text-rose-600" type="button" data-testid={`edit-${testid}-rm-${i}`}>x</button>
            </span>
          ))}
        </div>
      )}
      {hasSuggestions && (
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-wider text-indigo-500 mb-1 font-semibold flex items-center gap-1">
            <Sparkle size={10} weight="fill"/> AI suggestions &mdash; click + to add
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid={`edit-${testid}-suggestions`}>
            {suggestions.map((it, i) => (
              <span key={`sug-${it}-${i}`} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-white border border-dashed border-indigo-300 text-indigo-700 rounded">
                <button onClick={() => onPromote(it)} className="hover:text-emerald-700 font-bold" type="button" title="Add this" data-testid={`edit-${testid}-promote-${i}`}>+</button>
                {it}
                <button onClick={() => onDismiss(it)} className="hover:text-rose-600 text-zinc-400" type="button" title="Dismiss">&times;</button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RequisitionHistoryDialog — audit trail of edits
// ---------------------------------------------------------------------------
function RequisitionHistoryDialog({ requisitionId, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/requisitions/${requisitionId}/history`); setRows(r.data); }
      catch (e) { err(e); setRows([]); }
    })();
  }, [requisitionId]);

  const fmt = (v) => {
    if (v === null || v === undefined || v === "") return <span className="text-zinc-400 italic">empty</span>;
    if (Array.isArray(v)) return v.length ? v.join(", ") : <span className="text-zinc-400 italic">empty</span>;
    if (typeof v === "boolean") return v ? "Yes" : "No";
    const s = String(v);
    return s.length > 80 ? s.slice(0, 77) + "..." : s;
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="req-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClockCounterClockwise size={18}/>Edit history</DialogTitle>
          <DialogDescription>Every change to this requisition since it was created.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[68vh] overflow-y-auto py-2">
          {rows === null && <div className="text-zinc-500 text-sm">Loading...</div>}
          {Array.isArray(rows) && rows.length === 0 && (
            <div className="text-zinc-500 text-sm text-center py-8">No edits yet &mdash; this requisition is in its original posted state.</div>
          )}
          {Array.isArray(rows) && rows.map((h) => (
            <div key={h.id} className="border-b border-zinc-100 last:border-0 py-3" data-testid={`req-history-row-${h.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm font-medium text-zinc-900">{h.actor_name}
                  {h.actor_role && <Badge variant="outline" className="ml-2 text-[10px] capitalize">{String(h.actor_role).replace(/_/g, " ")}</Badge>}
                </div>
                <div className="text-xs text-zinc-500">{new Date(h.at).toLocaleString()}</div>
              </div>
              {h.scoring_impact && (
                <Badge className="mt-1 bg-amber-100 text-amber-900 text-[10px]"><Sparkle size={10} className="inline mr-0.5"/>Triggered AI re-score</Badge>
              )}
              <ul className="mt-2 space-y-1 pl-1">
                {(h.changes || []).map((c, i) => (
                  <li key={i} className="text-xs">
                    <span className="font-mono text-indigo-700">{c.field}</span>
                    <span className="text-zinc-400 mx-1">:</span>
                    <span className="text-rose-700 line-through">{fmt(c.from)}</span>
                    <span className="text-zinc-400 mx-1">&rarr;</span>
                    <span className="text-emerald-700 font-medium">{fmt(c.to)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
